import React, { useCallback, useEffect, useState } from "react";
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  Drawer,
  Paper,
  Stack,
  Switch,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import HistoryIcon from "@mui/icons-material/History";
import PeopleIcon from "@mui/icons-material/People";
import { toast } from "react-toastify";
import api from "../../api";
import { sanitizeStripeCheckoutUrl } from "../../utils/stripeCheckoutUrl";

const PURPLE = "#7c3aed";

/**
 * Subscription plans and one-time packs in one place.
 *
 * Plans were previously only reachable through the generic Master Forms CRUD,
 * which is why several of them (quarterly, semi-annual, yearly) shipped without
 * a Stripe price id. This page shows that gap explicitly.
 */
export default function SubscriptionPlans() {
  const [tab, setTab] = useState(0);

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      <Typography variant="h5" fontWeight={700} sx={{ mb: 2 }}>
        Subscription plans & packs
      </Typography>

      <Paper elevation={0} sx={{ borderRadius: 3, border: "1px solid #eef0f4", overflow: "hidden" }}>
        <Tabs
          value={tab}
          onChange={(_, v) => setTab(v)}
          sx={{
            px: 2,
            borderBottom: "1px solid #eef0f4",
            "& .MuiTab-root": { textTransform: "uppercase", fontWeight: 700, fontSize: 12 },
            "& .Mui-selected": { color: PURPLE },
            "& .MuiTabs-indicator": { bgcolor: PURPLE },
          }}
        >
          <Tab label="Plans" />
          <Tab label="Packs" />
        </Tabs>

        <Box sx={{ p: { xs: 2, md: 3 } }}>
          {tab === 0 ? <PlansTab /> : <BundlesTab />}
        </Box>
      </Paper>
    </Box>
  );
}

function formatPence(pence) {
  const value = Number(pence || 0) / 100;
  return `£${value.toFixed(2)}`;
}

/** Read-only view of the mobile plans, with the Stripe wiring called out. */
function PlansTab() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [historyPlan, setHistoryPlan] = useState(null);
  const [accessPlan, setAccessPlan] = useState(null);

  useEffect(() => {
    api
      .get("admin/master-form/mobile_subscription_plans", { params: { per_page: 100 } })
      .then((res) => {
        const data = res.data?.data;
        setRows(Array.isArray(data) ? data : data?.data || []);
      })
      .catch(() => toast.error("Could not load the plans."))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <CircularProgress size={28} />;

  const missingPrice = rows.filter((r) => r.is_active && !r.stripe_price_id);

  return (
    <Box>
      {missingPrice.length > 0 && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          {missingPrice.length} active plan{missingPrice.length > 1 ? "s have" : " has"} no Stripe
          price id and cannot be bought: {missingPrice.map((r) => r.name).join(", ")}. Add it in
          Master Forms → Mobile subscription plans.
        </Alert>
      )}

      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell sx={{ fontWeight: 700 }}>Plan</TableCell>
            <TableCell sx={{ fontWeight: 700 }}>Billing</TableCell>
            <TableCell sx={{ fontWeight: 700 }} align="right">Price</TableCell>
            <TableCell sx={{ fontWeight: 700 }}>Stripe</TableCell>
            <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
            <TableCell sx={{ fontWeight: 700 }}>Visibility</TableCell>
            <TableCell sx={{ fontWeight: 700 }} align="right">Price history</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((row) => (
            <TableRow key={row.id}>
              <TableCell sx={{ fontWeight: 600 }}>{row.name}</TableCell>
              <TableCell>{row.billing_interval || "—"}</TableCell>
              <TableCell align="right">{formatPence(row.price_pence)}</TableCell>
              <TableCell>
                <Chip
                  size="small"
                  label={row.stripe_price_id ? "Linked" : "Not linked"}
                  sx={
                    row.stripe_price_id
                      ? { bgcolor: "#dcfce7", color: "#166534", fontWeight: 600 }
                      : { bgcolor: "#fee2e2", color: "#991b1b", fontWeight: 600 }
                  }
                />
              </TableCell>
              <TableCell>{row.is_active ? "Active" : "Inactive"}</TableCell>
              <TableCell>
                {row.is_public === false ? (
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Tooltip title="Left out of every public plan list. Only the users you allow can see and buy it.">
                      <Chip size="small" label="Hidden" sx={{ bgcolor: "#fef3c7", color: "#92400e", fontWeight: 600 }} />
                    </Tooltip>
                    <Button
                      size="small"
                      startIcon={<PeopleIcon fontSize="small" />}
                      onClick={() => setAccessPlan(row)}
                      sx={{ textTransform: "none", color: PURPLE, fontWeight: 600 }}
                    >
                      Allowed users
                    </Button>
                  </Stack>
                ) : (
                  "Everyone"
                )}
              </TableCell>
              <TableCell align="right">
                <Tooltip title="Every price this plan has charged, and who is still on each">
                  <Button
                    size="small"
                    startIcon={<HistoryIcon fontSize="small" />}
                    onClick={() => setHistoryPlan(row)}
                    sx={{ textTransform: "none", color: PURPLE, fontWeight: 600 }}
                  >
                    View
                  </Button>
                </Tooltip>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>

      {historyPlan ? (
        <PlanPriceHistory plan={historyPlan} onClose={() => setHistoryPlan(null)} />
      ) : null}
      {accessPlan ? (
        <PlanAllowedUsers plan={accessPlan} onClose={() => setAccessPlan(null)} />
      ) : null}
    </Box>
  );
}

/**
 * Who may see and buy a hidden plan.
 *
 * A hidden plan (the £1 Weekly) is left out of every public plan list — site, portals and
 * the app. Adding someone here, e.g. a mobile tester, shows it to them alone.
 */
function PlanAllowedUsers({ plan, onClose }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [search, setSearch] = useState("");
  const [options, setOptions] = useState([]);
  const [searching, setSearching] = useState(false);
  const [selected, setSelected] = useState(null);
  const [note, setNote] = useState("");
  const [paymentLinks, setPaymentLinks] = useState({});

  const load = useCallback(() => {
    setLoading(true);
    return api
      .get(`admin/mobile-subscription-plans/${plan.id}/allowed-users`)
      .then((res) => setRows(res.data?.data || []))
      .catch(() => toast.error("Could not load the allowed users."))
      .finally(() => setLoading(false));
  }, [plan.id]);

  useEffect(() => {
    load();
  }, [load]);

  // Students, including those without app access yet. A parent email resolves to the child,
  // because the subscription sits on the student account.
  useEffect(() => {
    const term = search.trim();
    if (term.length < 2) {
      setOptions([]);
      return undefined;
    }
    const timer = setTimeout(() => {
      setSearching(true);
      api
        .get("admin/mobile-subscription-plans/user-search", { params: { search: term } })
        .then((res) => setOptions(res.data?.data || []))
        .catch(() => setOptions([]))
        .finally(() => setSearching(false));
    }, 300);
    return () => clearTimeout(timer);
  }, [search]);

  const grant = async () => {
    if (!selected) return;
    setBusy(true);
    try {
      const res = await api.post(`admin/mobile-subscription-plans/${plan.id}/allowed-users`, {
        user_id: selected.id,
        note: note.trim() || null,
      });
      toast.success(res.data?.message || "Access granted.");
      setSelected(null);
      setSearch("");
      setNote("");
      await load();
    } catch (e) {
      toast.error(e.response?.data?.message || "Could not grant access.");
    } finally {
      setBusy(false);
    }
  };

  const subscribe = async (row) => {
    setBusy(true);
    try {
      const res = await api.post(`admin/mobile-users/${row.id}/payment-link`, {
        plan_id: plan.id,
        send_email: true,
      });
      const url = sanitizeStripeCheckoutUrl(res.data?.data?.checkout_url);
      if (!url) throw new Error(res.data?.message || "No checkout URL returned.");
      setPaymentLinks((prev) => ({ ...prev, [row.id]: url }));
      toast.success(res.data?.message || "Payment link created.");
    } catch (e) {
      toast.error(e.response?.data?.message || e.message || "Could not create a payment link.");
    } finally {
      setBusy(false);
    }
  };

  const revoke = async (row) => {
    if (!window.confirm(`Remove ${row.name || row.email} from ${plan.name}?`)) return;
    setBusy(true);
    try {
      await api.delete(`admin/mobile-subscription-plans/${plan.id}/allowed-users/${row.id}`);
      toast.success("Access removed.");
      await load();
    } catch (e) {
      toast.error(e.response?.data?.message || "Could not remove access.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Drawer anchor="right" open onClose={onClose} PaperProps={{ sx: { width: { xs: "100%", sm: 640 } } }}>
      <Box sx={{ p: 3 }}>
        <Typography variant="h6" fontWeight={700}>
          {plan.name} — allowed users
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, mb: 2 }}>
          This plan is hidden from everyone else. Give access to the student account — a parent
          email finds their child — then send a payment link so they can subscribe.
        </Typography>

        <Stack spacing={1.5} sx={{ mb: 3, p: 2, border: "1px solid #eef0f4", borderRadius: 2 }}>
          <Autocomplete
            size="small"
            options={options}
            value={selected}
            loading={searching}
            filterOptions={(x) => x}
            onChange={(_, v) => setSelected(v)}
            onInputChange={(_, v) => setSearch(v)}
            isOptionEqualToValue={(a, b) => a.id === b.id}
            getOptionLabel={(o) => {
              const name = (o.full_name || "").trim() || "—";
              const access = o.is_allow_mobile ? "" : " · app access off";
              const via = o.via ? ` · child of ${o.via}` : "";
              return `${name} · ${o.email || ""}${access}${via}`;
            }}
            noOptionsText={search.trim().length < 2 ? "Type a name or email" : "No student accounts found"}
            renderInput={(params) => <TextField {...params} label="Find a user by name or email" />}
          />
          <TextField
            size="small"
            label="Note (optional)"
            placeholder="e.g. Mobile tester"
            value={note}
            onChange={(e) => setNote(e.target.value)}
          />
          <Box>
            <Button
              variant="contained"
              disabled={!selected || busy}
              onClick={grant}
              sx={{ textTransform: "none", bgcolor: PURPLE }}
            >
              Give access
            </Button>
          </Box>
        </Stack>

        {loading ? (
          <CircularProgress size={28} />
        ) : rows.length === 0 ? (
          <Alert severity="info">Nobody has access yet.</Alert>
        ) : (
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 700 }}>User</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Added</TableCell>
                <TableCell sx={{ fontWeight: 700 }} align="right">Subscribe</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((row) => (
                <TableRow key={row.id}>
                  <TableCell>
                    <Typography sx={{ fontWeight: 600, fontSize: 14 }}>{(row.name || "").trim() || "—"}</Typography>
                    <Typography variant="caption" color="text.secondary" display="block">
                      {row.email}
                    </Typography>
                    {row.note ? (
                      <Typography variant="caption" color="text.secondary" display="block">
                        {row.note}
                      </Typography>
                    ) : null}
                  </TableCell>
                  <TableCell>
                    {row.granted_at ? new Date(row.granted_at).toLocaleDateString("en-GB") : "—"}
                    {row.granted_by ? (
                      <Typography variant="caption" color="text.secondary" display="block">
                        by {row.granted_by}
                      </Typography>
                    ) : null}
                  </TableCell>
                  <TableCell align="right">
                    <Stack spacing={0.5} alignItems="flex-end">
                      {paymentLinks[row.id] ? (
                        <Typography variant="caption" sx={{ display: "block", wordBreak: "break-all", maxWidth: 220 }}>
                          {paymentLinks[row.id]}
                        </Typography>
                      ) : (
                        <Button
                          size="small"
                          disabled={busy}
                          onClick={() => subscribe(row)}
                          sx={{ textTransform: "none", color: PURPLE, fontWeight: 600, minWidth: 0 }}
                        >
                          Payment link
                        </Button>
                      )}
                      <Button
                        size="small"
                        color="error"
                        disabled={busy}
                        onClick={() => revoke(row)}
                        sx={{ textTransform: "none", minWidth: 0 }}
                      >
                        Remove
                      </Button>
                    </Stack>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}

        <Button onClick={onClose} sx={{ mt: 3, textTransform: "none", display: "block" }}>
          Close
        </Button>
      </Box>
    </Drawer>
  );
}

/**
 * Every price a plan has ever charged.
 *
 * Editing a price no longer rewrites the old one: the previous row is closed and a new one
 * opened, and everyone already subscribed stays pointed at the row they joined on. This is
 * where an admin confirms that — and sees how many people are still on an older amount
 * before deciding whether a change is safe.
 */
function PlanPriceHistory({ plan, onClose }) {
  const [rows, setRows] = useState([]);
  const [promo, setPromo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  // Stripe price id being edited, keyed by tier id. A legacy tier rebuilt from payment
  // history has no Price id, so it cannot be charged until an admin supplies one.
  const [draftPriceId, setDraftPriceId] = useState({});
  const [adding, setAdding] = useState(false);
  const [newTier, setNewTier] = useState({ price_pence: "", stripe_price_id: "", label: "" });

  const load = useCallback(() => {
    setLoading(true);
    return api
      .get(`admin/mobile-subscription-plans/${plan.id}/prices`)
      .then((res) => {
        setRows(Array.isArray(res.data?.data) ? res.data.data : []);
        setPromo(res.data?.promo || null);
        setDraftPriceId({});
      })
      .catch((e) => setError(e.response?.data?.message || "Could not load the price history."))
      .finally(() => setLoading(false));
  }, [plan.id]);

  useEffect(() => {
    load();
  }, [load]);

  const saveStripeId = async (row) => {
    const value = (draftPriceId[row.id] ?? "").trim();
    setBusy(true);
    setError("");
    try {
      await api.patch(`admin/mobile-subscription-plans/${plan.id}/prices/${row.id}`, {
        stripe_price_id: value || null,
      });
      toast.success("Stripe price saved.");
      await load();
    } catch (e) {
      setError(e.response?.data?.message || "Could not save the Stripe price id.");
    } finally {
      setBusy(false);
    }
  };

  const addTier = async () => {
    const pounds = Number(newTier.price_pence);
    if (!Number.isFinite(pounds) || pounds < 0) {
      setError("Enter the tier amount in pence, e.g. 999 for £9.99.");
      return;
    }
    setBusy(true);
    setError("");
    try {
      await api.post(`admin/mobile-subscription-plans/${plan.id}/prices`, {
        price_pence: Math.round(pounds),
        stripe_price_id: newTier.stripe_price_id.trim() || null,
        label: newTier.label.trim() || null,
        // Closed by default: a new tier added by hand is almost always a historical one being
        // recorded, not a change to what new customers pay. Editing the plan does that.
        effective_to: new Date().toISOString().slice(0, 19).replace("T", " "),
      });
      toast.success("Price tier added.");
      setNewTier({ price_pence: "", stripe_price_id: "", label: "" });
      setAdding(false);
      await load();
    } catch (e) {
      setError(e.response?.data?.message || "Could not add the price tier.");
    } finally {
      setBusy(false);
    }
  };

  const removeTier = async (row) => {
    setBusy(true);
    setError("");
    try {
      await api.delete(`admin/mobile-subscription-plans/${plan.id}/prices/${row.id}`);
      toast.success("Price tier removed.");
      await load();
    } catch (e) {
      setError(e.response?.data?.message || "Could not remove the price tier.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Drawer anchor="right" open onClose={onClose} PaperProps={{ sx: { width: { xs: "100%", sm: 540 } } }}>
      <Box sx={{ p: 3 }}>
        <Typography variant="h6" fontWeight={700}>
          {plan.name} — price history
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, mb: 2 }}>
          Changing this plan&apos;s price adds a new row here. Subscribers stay on the row they
          joined on, so a price rise never reaches anyone who has already subscribed.
        </Typography>

        {promo?.eligible ? (
          <Alert severity={promo.is_open ? "success" : "info"} sx={{ mb: 2 }}>
            {promo.is_open
              ? `Early-adopter price ${formatPence(promo.price_pence)} is being offered${
                  promo.ends_at ? ` until ${new Date(promo.ends_at).toLocaleDateString("en-GB")}` : ""
                }. Anyone who subscribes now keeps it for the life of their plan.`
              : "No early-adopter price is being offered on this plan right now. Set a promo price, a Stripe price id and an end date in Master Forms to open the window."}
          </Alert>
        ) : (
          <Alert severity="info" sx={{ mb: 2 }}>
            Early-adopter pricing applies to the Standard monthly plan only, so promo fields on
            this plan are ignored.
          </Alert>
        )}

        {error ? <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert> : null}

        {loading ? (
          <CircularProgress size={28} />
        ) : (
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 700 }}>Price</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>From</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Until</TableCell>
                <TableCell sx={{ fontWeight: 700 }} align="right">Subscribers</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((row) => (
                <TableRow key={row.id}>
                  <TableCell sx={{ fontWeight: 600 }}>
                    {formatPence(row.price_pence)}
                    {row.is_promo ? (
                      <Chip size="small" label="Promo" sx={{ ml: 1, bgcolor: "#ede9fe", color: PURPLE }} />
                    ) : null}
                    {row.is_current ? (
                      <Chip size="small" label="Current" sx={{ ml: 1, bgcolor: "#dcfce7", color: "#166534" }} />
                    ) : null}
                    {row.label ? (
                      <Typography variant="caption" color="text.secondary" display="block">
                        {row.label}
                      </Typography>
                    ) : null}
                    {/* A tier with no Stripe Price cannot be charged — which matters most for
                        a legacy tier rebuilt from payment history, because its subscribers
                        cannot be re-charged after a failed card until this is filled in. */}
                    <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 0.5 }}>
                      <TextField
                        size="small"
                        placeholder={row.stripe_price_id ? "" : "price_… (required to charge)"}
                        value={draftPriceId[row.id] ?? row.stripe_price_id ?? ""}
                        onChange={(e) =>
                          setDraftPriceId((d) => ({ ...d, [row.id]: e.target.value }))
                        }
                        error={!row.stripe_price_id}
                        sx={{ minWidth: 230, "& input": { fontSize: 12, fontFamily: "monospace" } }}
                      />
                      <Button
                        size="small"
                        disabled={
                          busy ||
                          draftPriceId[row.id] === undefined ||
                          draftPriceId[row.id] === (row.stripe_price_id ?? "")
                        }
                        onClick={() => saveStripeId(row)}
                        sx={{ textTransform: "none" }}
                      >
                        Save
                      </Button>
                    </Stack>
                  </TableCell>
                  <TableCell>
                    {row.effective_from
                      ? new Date(row.effective_from).toLocaleDateString("en-GB")
                      : "—"}
                  </TableCell>
                  <TableCell>
                    {row.effective_to
                      ? new Date(row.effective_to).toLocaleDateString("en-GB")
                      : "—"}
                  </TableCell>
                  <TableCell align="right">
                    {row.subscriber_count}
                    {row.subscriber_count === 0 ? (
                      <Button
                        size="small"
                        color="error"
                        disabled={busy}
                        onClick={() => removeTier(row)}
                        sx={{ textTransform: "none", minWidth: 0, ml: 1 }}
                      >
                        Delete
                      </Button>
                    ) : null}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}

        {/* Recording a tier that already existed before this system did — the usual reason is
            a legacy price reconstructed from payment history that needs its Stripe Price. */}
        {adding ? (
          <Box sx={{ mt: 2, p: 2, border: "1px solid #eef0f4", borderRadius: 2 }}>
            <Typography sx={{ fontWeight: 700, mb: 1.5 }}>Add a historical tier</Typography>
            <Stack spacing={1.5}>
              <TextField
                size="small"
                label="Amount in pence"
                placeholder="999 = £9.99"
                value={newTier.price_pence}
                onChange={(e) => setNewTier((t) => ({ ...t, price_pence: e.target.value }))}
              />
              <TextField
                size="small"
                label="Stripe price ID"
                placeholder="price_…"
                value={newTier.stripe_price_id}
                onChange={(e) => setNewTier((t) => ({ ...t, stripe_price_id: e.target.value }))}
              />
              <TextField
                size="small"
                label="Label"
                placeholder="e.g. Early adopter price"
                value={newTier.label}
                onChange={(e) => setNewTier((t) => ({ ...t, label: e.target.value }))}
              />
              <Typography variant="caption" color="text.secondary">
                Added as a closed tier. To change what new customers pay, edit the plan itself
                in Master Forms — that opens a new current price and leaves subscribers alone.
              </Typography>
              <Stack direction="row" spacing={1}>
                <Button variant="contained" disabled={busy} onClick={addTier} sx={{ textTransform: "none", bgcolor: PURPLE }}>
                  Add tier
                </Button>
                <Button disabled={busy} onClick={() => setAdding(false)} sx={{ textTransform: "none" }}>
                  Cancel
                </Button>
              </Stack>
            </Stack>
          </Box>
        ) : (
          <Button
            onClick={() => setAdding(true)}
            disabled={loading || busy}
            sx={{ mt: 2, textTransform: "none", color: PURPLE, fontWeight: 600 }}
          >
            + Add a historical tier
          </Button>
        )}

        <Button onClick={onClose} sx={{ mt: 3, textTransform: "none", display: "block" }}>
          Close
        </Button>
      </Box>
    </Drawer>
  );
}

/** Editable prices and access windows for the two one-time packs. */
function BundlesTab() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [savingId, setSavingId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get("admin/resource-bundles");
      setRows(res.data?.data || []);
    } catch {
      toast.error("Could not load the packs.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const patch = (id, field, value) =>
    setRows((current) => current.map((r) => (r.id === id ? { ...r, [field]: value } : r)));

  const save = async (row) => {
    setSavingId(row.id);
    try {
      await api.put(`admin/resource-bundles/${row.id}`, {
        name: row.name,
        price: Number(row.price) || 0,
        access_months: row.access_months === "" || row.access_months == null ? null : Number(row.access_months),
        is_active: !!row.is_active,
      });
      await load();
      toast.success("Pack saved.");
    } catch (err) {
      toast.error(err?.response?.data?.message || "Could not save that pack.");
    } finally {
      setSavingId(null);
    }
  };

  if (loading) return <CircularProgress size={28} />;

  return (
    <Stack spacing={2}>
      <Alert severity="info">
        Packs are sold separately. Changing a price clears the Stripe link — a new Stripe price is
        created automatically on the next checkout, because Stripe prices cannot be edited.
      </Alert>

      {rows.map((row) => (
        <Paper
          key={row.id}
          elevation={0}
          sx={{ p: 2.5, borderRadius: 3, border: "1px solid #eef0f4" }}
        >
          <Stack
            direction={{ xs: "column", md: "row" }}
            spacing={2}
            alignItems={{ md: "center" }}
          >
            <TextField
              label="Name"
              value={row.name || ""}
              onChange={(e) => patch(row.id, "name", e.target.value)}
              sx={{ flex: 2, minWidth: 200 }}
            />
            <TextField
              label="Price (£)"
              type="number"
              inputProps={{ min: 0, step: "0.01" }}
              value={row.price ?? ""}
              onChange={(e) => patch(row.id, "price", e.target.value)}
              sx={{ width: 140 }}
            />
            <TextField
              label="Access (months)"
              type="number"
              inputProps={{ min: 1 }}
              helperText="Blank = lifetime"
              value={row.access_months ?? ""}
              onChange={(e) => patch(row.id, "access_months", e.target.value)}
              sx={{ width: 170 }}
            />
            <Box sx={{ textAlign: "center" }}>
              <Typography variant="caption" color="text.secondary" display="block">
                On sale
              </Typography>
              <Switch
                checked={!!row.is_active}
                onChange={(e) => patch(row.id, "is_active", e.target.checked)}
              />
            </Box>
            <Button
              variant="contained"
              onClick={() => save(row)}
              disabled={savingId === row.id}
              sx={{ bgcolor: PURPLE, "&:hover": { bgcolor: "#6d28d9" } }}
            >
              {savingId === row.id ? "Saving…" : "Save"}
            </Button>
          </Stack>

          <Typography variant="caption" color="text.secondary" sx={{ mt: 1.5, display: "block" }}>
            Covers {row.item_count} items · {row.purchase_count} sold ·{" "}
            {row.stripe_price_id ? "Stripe linked" : "Stripe price created on first sale"}
          </Typography>
        </Paper>
      ))}
    </Stack>
  );
}
